/**
 * screens/Reconciliation/components/ReconCard
 * ──────────────────────────────────────────────────────────────────────────
 * One cash count.
 *
 * The card is four bands: a header naming the holder, period, kind and status;
 * the LEGS in a hairline grid; a footer carrying the consequence sentence and
 * the two actions; and a mono strip saying who counted, and when.
 *
 * WHICH LEGS APPEAR depends on the kind, because the two kinds measure
 * different things:
 *   limit       Expected (limit) · Counted · Variance
 *   collection  Owed (Billing) · Collected · Deposited · Variance
 *
 * THE CONSEQUENCE SENTENCE IS THE API'S, rendered verbatim. What a variance
 * costs someone — "This INR 80.00 surplus will be posted to Billing." — is the
 * backend's to state, because it is the thing that knows the routing. Composing
 * it here would be this app guessing at an accounting outcome.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { StyleSheet, Text, View } from 'react-native';
import { Button, Card, HairlineGrid, StatusPill } from '../../../components';
import { neutral } from '../../../theme/colors';
import { gridMin } from '../../../theme/spacing';
import { mono, numeric, sans, size as fontSize, leading } from '../../../theme/typography';
import { dateTime } from '../../../lib/dates';
import { dotted } from '../../../lib/format';
import { formatMinor } from '../../../lib/money';
import { reconKindLabel, reconStatus } from '../../../lib/status';
import { needsRouting, type Reconciliation } from '../../../schemas/reconciliation';

export interface ReconCardProps {
  recon: Reconciliation;
  onViewLedger: () => void;
  onWaive: () => void;
  onRoute: () => void;
}

/** One leg of the count: a label, a mono figure, and where the figure came from. */
interface Leg {
  key: string;
  label: string;
  /** Minor units, or null when this leg was not recorded. */
  value: string | null;
  source: string;
}

function legsFor(recon: Reconciliation): Leg[] {
  if (recon.kind === 'collection') {
    return [
      { key: 'owed', label: 'Owed', value: recon.owed_minor, source: 'from Billing' },
      { key: 'collected', label: 'Collected', value: recon.collected_minor, source: 'counted' },
      { key: 'deposited', label: 'Deposited', value: recon.deposited_minor, source: 'banked' },
      { key: 'variance', label: 'Variance', value: recon.variance_minor, source: 'difference' },
    ];
  }
  return [
    { key: 'expected', label: 'Expected', value: recon.expected_minor, source: 'limit' },
    { key: 'counted', label: 'Counted', value: recon.counted_minor, source: 'counted' },
    { key: 'variance', label: 'Variance', value: recon.variance_minor, source: 'difference' },
  ];
}

export function ReconCard({ recon, onViewLedger, onWaive, onRoute }: ReconCardProps) {
  const state = reconStatus(recon.status);
  const open = needsRouting(recon);

  return (
    <Card>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text numberOfLines={1} style={styles.holder}>
            {recon.holder}
          </Text>
          <Text numberOfLines={1} style={styles.headerMeta}>
            {dotted(recon.period, reconKindLabel(recon.kind))}
          </Text>
          <StatusPill label={state.label} tone={state.tone} />
        </View>
        <Button label="View ledger" size="xs" variant="outline" onPress={onViewLedger} />
      </View>

      <HairlineGrid min={gridMin.reconLeg}>
        {legsFor(recon).map((leg) => (
          <View key={leg.key} style={styles.leg}>
            <Text style={styles.legLabel}>{leg.label}</Text>
            <Text style={styles.legValue}>
              {leg.value === null ? '—' : `${recon.cur} ${formatMinor(leg.value, recon.cur)}`}
            </Text>
            <Text style={styles.legSource}>{leg.source}</Text>
          </View>
        ))}
      </HairlineGrid>

      <View style={styles.consequence}>
        <Text style={styles.consequenceText}>{recon.consequence}</Text>
        <View style={styles.actions}>
          {/* Once resolved, the reference IS the record — no buttons remain. */}
          {!open && !!recon.route_ref && (
            <Text style={styles.routed}>routed · {recon.route_ref}</Text>
          )}
          {open && <Button label="Waive" size="md" variant="outline" onPress={onWaive} />}
          {open && <Button label="Route variance" size="md" variant="primary" onPress={onRoute} />}
        </View>
      </View>

      <View style={styles.foot}>
        <Text style={styles.footText}>
          {dotted(
            recon.reconciled_at ? `counted ${dateTime(recon.reconciled_at)}` : 'not yet resolved',
            recon.reconciled_by ? `by ${recon.reconciled_by}` : null,
          )}
        </Text>
      </View>
    </Card>
  );
}

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 12,
    paddingHorizontal: 15,
    backgroundColor: neutral.surface2,
    borderBottomWidth: 1,
    borderBottomColor: neutral.border,
  },
  headerLeft: { flexDirection: 'row', alignItems: 'center', gap: 10, minWidth: 0, flexShrink: 1, flexWrap: 'wrap' },
  holder: { ...sans('600'), fontSize: fontSize.body, color: neutral.ink },
  headerMeta: { ...mono('400'), fontSize: fontSize.metaSm, color: neutral.grey600 },

  leg: { backgroundColor: neutral.surface, paddingVertical: 13, paddingHorizontal: 15 },
  legLabel: {
    ...sans('400'),
    fontSize: fontSize.tableHeader,
    letterSpacing: 0.63,
    textTransform: 'uppercase',
    color: neutral.grey600,
    marginBottom: 6,
  },
  legValue: { ...mono('400'), fontSize: fontSize.lg, ...numeric, color: neutral.ink },
  legSource: { ...sans('400'), fontSize: fontSize.metaSm, color: neutral.grey400, marginTop: 3 },

  consequence: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    flexWrap: 'wrap',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderTopWidth: 1,
    borderTopColor: neutral.border,
  },
  consequenceText: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.ink2,
    maxWidth: 640,
    flexShrink: 1,
  },
  actions: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 },
  routed: { ...mono('400'), fontSize: fontSize.metaSm, ...numeric, color: neutral.grey600 },

  foot: {
    paddingVertical: 8,
    paddingHorizontal: 15,
    backgroundColor: neutral.surface2,
    borderTopWidth: 1,
    borderTopColor: neutral.border,
  },
  footText: { ...mono('400'), fontSize: fontSize.tableHeader, ...numeric, color: neutral.grey400 },
});

export default ReconCard;
